import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { SyncModelKind, SyncUpdateValue, OptimisticSyncUpdate } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { SubscriptionObservable } from "./SubscriptionObservable";
import { SubscriptionModelData } from "./types";
import { SubscriptionIndexes } from "./SubscriptionIndexes";
import { makeObservable, override, reaction, computed, action, observable } from "mobx";
import { Maybe } from "@/domains/common/types";
import { AppStore } from "@/store/AppStore";
import { DateTime } from "luxon";
import { GatedFeature, PlanId } from "@/store/feature-gates/types";
import { noop } from "lodash-es";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { PaywallContext } from "@/domains/metrics/tracked-event";

// TODO: make this configurable from the backend
// https://linear.app/mem-labs/issue/MEM-12522/make-the-free-tier-limit-configurable-on-the-backend
const FREE_LIMIT = 25;

export class AppStoreSubscriptionStore extends BaseSyncModelStore<
  SubscriptionObservable,
  SubscriptionModelData
> {
  // Centralized upsell modal state
  upsellModalIsOpen: boolean = false;
  upsellModalFeature?: GatedFeature;
  upsellModalContext?: PaywallContext;

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.Subscription, ...injectedDeps });

    makeObservable<this>(this, {
      getPersonalSubscription: false,
      usePersonalSubscription: false,
      createSyncModel: false,
      computeIndexes: override,
      personalSubscription: computed,
      checkFreeTierNoteLimit: false,
      personalPlanId: computed,
      // Upsell modal state/actions
      upsellModalIsOpen: observable,
      upsellModalFeature: observable,
      upsellModalContext: observable,
      openUsageLimitUpsell: action,
      closeUpsell: action,
    });
  }

  usePersonalSubscription() {
    useEffectOnMount(() => {
      // Make sure we have a personal subscription loaded to memory for fast access.
      const dispose = reaction(() => this.personalSubscription, noop);
      return dispose;
    });
  }

  openUsageLimitUpsell(params: {
    used?: number;
    limit?: number;
    feature: GatedFeature;
    context?: PaywallContext;
  }) {
    // We don't currently surface used/limit inside the modal UI, but we might in the future
    this.upsellModalFeature = params.feature;
    this.upsellModalContext = params.context;
    this.upsellModalIsOpen = true;
  }

  closeUpsell = () => {
    this.upsellModalIsOpen = false;
    this.upsellModalFeature = undefined;
    this.upsellModalContext = undefined;
  };

  createSyncModel(data: SyncUpdateValue<SubscriptionModelData>): SubscriptionObservable {
    return new SubscriptionObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  /**
   * Synchronous best-effort read of the current user's plan.
   * Returns undefined until a subscription is available locally.
   */
  public get personalPlanId(): PlanId | undefined {
    const primarySpaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    if (!primarySpaceAccountId) return undefined;

    const subscriptionIds = Array.from(this.ids.values());
    if (subscriptionIds.length === 0) return undefined;

    const subscriptions = this.getMany(subscriptionIds);
    const sub = subscriptions.find(s => s.spaceAccountId === primarySpaceAccountId);
    if (!sub) return undefined;

    return sub.isPaid ? PlanId.Paid : PlanId.Free;
  }

  public computeIndexes({
    store,
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<SubscriptionModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<SubscriptionModelData>[];
  }): Record<string, unknown> {
    return new SubscriptionIndexes({ store, remoteData, optimisticUpdates }).indexes;
  }

  get personalSubscription(): SubscriptionObservable | undefined {
    return this.all.find(
      subscription =>
        subscription.spaceAccountId === this.store.spaceAccounts.myPersonalSpaceAccountId
    );
  }

  async getPersonalSubscription(): Promise<SubscriptionObservable | undefined> {
    if (this.personalSubscription) return this.personalSubscription;
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    const allSubscriptions = await this.getAllAsync();
    return allSubscriptions.find(subscription => subscription.spaceAccountId === spaceAccountId);
  }

  public async checkFreeTierNoteLimit(): Promise<{
    canCreate: boolean;
    used: number;
    limit: number;
  }> {
    const subscription = this.personalSubscription;

    const termAgo = DateTime.now().minus({ weeks: 1 });
    const notesThisTerm = await this.store.notes.countMyNotesCreatedSince(termAgo);

    // Paid users have unlimited notes

    if (subscription?.isPaid) {
      return { canCreate: true, used: notesThisTerm, limit: Infinity };
    }

    return {
      canCreate: notesThisTerm < FREE_LIMIT,
      used: notesThisTerm,
      limit: FREE_LIMIT,
    };
  }
}
